import React, {useState} from "react";
import {
    Box,
    Button,
    Flex,
    VStack,
    Text,
    Textarea,
    Input,
} from "@chakra-ui/react";
import {ToastContainer, toast, Bounce} from 'react-toastify';
import {useTranslation} from "react-i18next";
import {useForm, Controller} from "react-hook-form";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {Select} from "chakra-react-select";
import WebApp from "@twa-dev/sdk";
import {Field} from "@chakra-ui/react";
import {useNavigate, useParams} from "react-router-dom";

type Reminder = {
    text: string;
    remindAt: string;
    category?: string;
};

type ReminderDto = {
    text: string,
    dateTime: string,
    category: string
};


type FormValues = {
    text: string;
    date: Date;
    time: Date;
    category: { value: string; label: string };
};

function CreateReminderPage() {
    const [reminder, setReminder] = useState<Reminder | undefined>(undefined);
    const [loading, setLoading] = useState(false);
    const {t} = useTranslation();
    const navigate = useNavigate();
    const { lang } = useParams();

    const categories = [
        {value: "work", label: t("categories.work")},
        {value: "personal", label: t("categories.personal")},
        {value: "health", label: t("categories.health")},
        {value: "family", label: t("categories.family")},
        {value: "finance", label: t("categories.finance")},
        {value: "shopping", label: t("categories.shopping")},
        {value: "study", label: t("categories.study")},
        {value: "events", label: t("categories.events")},
        {value: "travel", label: t("categories.travel")},
        {value: "other", label: t("categories.other")},
    ];

    const {
        register,
        handleSubmit,
        control,
        reset,
        formState: {errors},
    } = useForm<FormValues>();

    const isToday = (someDate: Date | null) => {
        if (!someDate) return false;
        const today = new Date();
        return (
            someDate.getDate() === today.getDate() &&
            someDate.getMonth() === today.getMonth() &&
            someDate.getFullYear() === today.getFullYear()
        );
    };

    const createReminder = async (dto: ReminderDto) => {
        setLoading(true)
        try {
            //Todo показывать загрузку на всей странице пока не загрузится
            const initData = WebApp?.initData;
            const response = await fetch("/api/ai-notification/reminders", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "X-Telegram-InitData": initData ?? "",
                },
                body: JSON.stringify(dto)
            });

            if (!response.ok) throw new Error("Request failed");

            const data: Reminder = await response.json();
            setReminder(data);

            reset({
                text: '',
                date: data.remindAt ? new Date(data.remindAt) : new Date(),
                time: data.remindAt ? new Date(data.remindAt) : new Date(new Date().getTime() + 15 * 60 * 1000),
                category: categories.find((cat) => cat.value === data.category)
            });
            toast.success(t("reminders.created"), {
                position: "top-right",
                autoClose: 5000,
                progress: undefined,
                theme: "dark",
                transition: Bounce,
            });
        } catch (error) {
            //Todo не удалось создать напоминание toast https://fkhadra.github.io/react-toastify/introduction/
        } finally {
            setLoading(false)
        }
    };

    function onSubmit(values: FormValues) {
        const now = new Date();

        const combinedDateTime = new Date(values.date);
        combinedDateTime.setHours(values.time.getHours());
        combinedDateTime.setMinutes(values.time.getMinutes());

        if (combinedDateTime < now) {
            toast.error(t("reminders.cannotBeInPast"), {
                position: "top-right",
                autoClose: 5000,
                progress: undefined,
                theme: "dark",
                transition: Bounce,
            });
            return;
        }

        const formatted = {
            text: values.text,
            dateTime: combinedDateTime.toISOString(),
            category: values.category.value
        };
        createReminder(formatted)
    }

    const backToReminder = () => {
        navigate(`/${lang}/reminders`);
    }

    return (
        <Box maxW="md" mx="auto" mt={6} p={4}>
            <Text fontSize="2xl" fontWeight="bold" mb={4}>
                {t("reminders.createReminder")}
            </Text>

            <VStack gap={3} align="stretch">
                <form onSubmit={handleSubmit(onSubmit)}>
                    <Field.Root isInvalid={!!errors.text} mb={4}>
                        <Field.Label htmlFor="text">
                            {t("reminders.text")}
                            <span style={{color: "red"}}> *</span>
                        </Field.Label>
                        <Textarea
                            id="text"
                            placeholder={t("reminders.text")}
                            {...register("text", {
                                required: t("reminders.textRequired")
                            })}
                        />
                    </Field.Root>

                    <Flex
                        mb={4}
                        wrap="wrap"
                        direction="row"
                        w="100%"
                        gap={4}
                        justify="space-between"
                        align="start"
                    >
                        <Controller
                            control={control}
                            rules={{required: t("reminders.textRequired")}}
                            name="date"
                            render={({field}) => (
                                <Field.Root mb={0} w="100%">
                                    <Field.Label htmlFor="date">
                                        {t("reminders.date")}
                                        <span style={{color: "red"}}> *</span>
                                    </Field.Label>
                                    <DatePicker
                                        id="date"
                                        dateFormat="yyyy-MM-dd"
                                        customInput={<Input w="100%"/>}
                                        selected={field.value}
                                        onChange={field.onChange}
                                        minDate={new Date()}
                                    />
                                </Field.Root>
                            )}
                        />

                        <Controller
                            control={control}
                            rules={{required: t("reminders.textRequired")}}
                            name="time"
                            render={({field}) => {
                                const selectedDate = control._formValues.date;
                                const now = new Date();

                                return (
                                    <Field.Root mb={4} w="100%">
                                        <Field.Label htmlFor="time">
                                            {t("reminders.time")}
                                            <span style={{color: "red"}}> *</span>
                                        </Field.Label>
                                        <DatePicker
                                            showTimeSelect
                                            showTimeSelectOnly
                                            timeCaption="Time"
                                            timeIntervals={5}
                                            dateFormat="HH:mm"
                                            customInput={<Input w="100%"/>}
                                            selected={field.value}
                                            onChange={field.onChange}
                                            minTime={
                                                isToday(selectedDate)
                                                    ? now
                                                    : new Date(0, 0, 0, 0, 0)
                                            }
                                            maxTime={new Date(0, 0, 0, 23, 59)}
                                        />
                                    </Field.Root>
                                );
                            }}
                        />
                    </Flex>

                    <Controller
                        control={control}
                        rules={{required: t("reminders.textRequired")}}
                        name="category"
                        render={({field}) => (
                            <Field.Root mb={0} w="100%">
                                <Field.Label htmlFor="category">
                                    {t("reminders.category")}
                                    <span style={{color: "red"}}> *</span>
                                </Field.Label>
                                <Select
                                    options={categories}
                                    size="sm"
                                    value={field.value}
                                    onChange={field.onChange}
                                />
                            </Field.Root>
                        )}
                    />

                    <Button mt={4} colorScheme="teal" type="submit" loading={loading}>
                        {t("reminders.create")}
                    </Button>
                </form>
            </VStack>
            <Button
                mt={6}
                variant="outline"
                colorScheme="gray"
                onClick={backToReminder}
            >
                {t("reminders.backToReminders")}
            </Button>
            <ToastContainer/>
        </Box>
    );
}

export default CreateReminderPage;